const React = require('react');
const { createElement: h, useState, useEffect, useRef } = React;
require('../App.css');

// A showcase video restarts from frame 0 once it is (almost) fully in view,
// and pauses as soon as it scrolls out — only the video you are looking at plays.
var FULLY_VISIBLE_RATIO = 0.95;

function ShowcaseVideo(props) {
  var videoRef = useRef(null);

  useEffect(function() {
    var el = videoRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.intersectionRatio >= FULLY_VISIBLE_RATIO) {
          el.currentTime = 0;
          var played = el.play();
          if (played && played.catch) played.catch(function() {});
        } else {
          el.pause();
        }
      });
    }, { threshold: [0, FULLY_VISIBLE_RATIO, 1] });

    observer.observe(el);
    return function() { observer.disconnect(); };
  }, []);

  return h('video', {
    ref: videoRef,
    src: props.src,
    poster: props.poster,
    className: 'showcase-item__visual-img',
    muted: true,
    loop: true,
    playsInline: true,
    preload: 'metadata',
    'aria-label': props.label
  });
}

function SectionGroup(props) {
  var section = props.section || {};
  var index = typeof props.index === 'number' ? props.index : 0;
  var items = Array.isArray(section.items) ? section.items : [];
  if (!items.length) return null;
  var parity = index % 2 === 0 ? 'odd' : 'even';

  return h('section', { className: 'marketing-section marketing-section--' + parity },
    h('div', { className: 'marketing-section__header' },
      h('span', { className: 'marketing-section__eyebrow' }, String(index + 1).padStart(2, '0')),
      h('h2', { className: 'marketing-section__title' }, section.title || '')
    ),
    h('div', { className: 'marketing-grid' },
      items.map(function(item, i) {
        var imageStyle = item.image
          ? { '--card-img-hue': item.image.hue || '220' }
          : null;
        return h('article', { key: item.title, className: 'marketing-card' + (item.image ? ' marketing-card--has-image' : ''), style: imageStyle },
          item.image ? h('div', { className: 'marketing-card__image' }) : null,
          h('div', { className: 'marketing-card__index' }, String(i + 1).padStart(2, '0')),
          h('h3', { className: 'marketing-card__title' }, item.title || ''),
          h('p', { className: 'marketing-card__copy' }, item.copy || '')
        );
      })
    )
  );
}

function ShowcaseSection(props) {
  var sections = props.sections || [];
  var allItems = [];
  sections.forEach(function(section) {
    (section.items || []).forEach(function(item) {
      allItems.push({ item: item, label: section.title });
    });
  });
  return h('div', { className: 'showcase-list' },
    allItems.map(function(entry, idx) {
      var isReverse = idx % 2 !== 0;
      // Items with a video show the video alone — no still image, no poster
      // frame. Only the image-only items fall back to an <img>.
      var imgSrc = typeof entry.item.image === 'string' ? '/' + entry.item.image : '/img1.png';
      return h('div', {
        key: entry.item.title,
        className: 'showcase-item' + (isReverse ? ' showcase-item--reverse' : '')
      },
        h('div', { className: 'showcase-item__text' },
          h('span', { className: 'showcase-item__eyebrow' }, entry.label),
          h('h3', { className: 'showcase-item__title' }, entry.item.title || ''),
          h('p', { className: 'showcase-item__copy' }, entry.item.copy || '')
        ),
        h('div', { className: 'showcase-item__visual' },
          typeof entry.item.video === 'string'
            ? h(ShowcaseVideo, {
                src: '/' + entry.item.video,
                label: entry.item.title || 'AlleFlow workflow canvas'
              })
            : h('img', { src: imgSrc, alt: entry.item.title || 'AlleFlow workflow canvas', className: 'showcase-item__visual-img' })
        )
      );
    })
  );
}

function UseCaseGrid(props) {
  var sections = props.sections || [];
  var allItems = [];
  sections.forEach(function(section) {
    (section.items || []).forEach(function(item) {
      allItems.push({ item: item, label: section.title });
    });
  });
  return h('div', { className: 'usecase-grid' },
    allItems.map(function(entry) {
      var item = entry.item;
      var tags = Array.isArray(item.tags) ? item.tags : [];
      var imgStyle = item.image ? { '--card-img-hue': item.image.hue || '220' } : {};
      return h('article', { key: item.title, className: 'usecase-card', style: imgStyle },
        h('div', { className: 'usecase-card__body' },
          h('div', { className: 'usecase-card__meta' },
            h('div', { className: 'usecase-card__badge' },
              h('span', { className: 'usecase-card__icon' }),
              h('span', { className: 'usecase-card__category' }, entry.label)
            ),
            h('span', { className: 'usecase-card__menu' }, '⋯')
          ),
          h('h3', { className: 'usecase-card__title' }, item.title || ''),
          tags.length ? h('p', { className: 'usecase-card__tags' },
            tags.map(function(tag) {
              return h('span', { key: tag, className: 'usecase-card__tag' }, tag);
            })
          ) : null
        ),
        h('div', { className: 'usecase-card__image' })
      );
    })
  );
}

function BetaBlock(props) {
  var perks = Array.isArray(props.perks) ? props.perks : [];
  return h('div', { className: 'beta-block' },
    h('div', { className: 'beta-block__card' },
      h('span', { className: 'beta-block__badge' }, 'Beta'),
      h('p', { className: 'beta-block__price' }, 'Gratis'),
      h('p', { className: 'beta-block__sub' }, 'Intet kreditkort påkrævet. Fuld adgang under beta.'),
      perks.length ? h('ul', { className: 'beta-block__perks' },
        perks.map(function(perk) {
          return h('li', { key: perk, className: 'beta-block__perk' },
            h('span', { className: 'beta-block__check' }, '✓'),
            perk
          );
        })
      ) : null,
      h('button', { className: 'btn btn--primary beta-block__cta', type: 'button', onClick: props.onGetStarted }, 'Begynd at bygge gratis')
    )
  );
}

function CreditFeature(props) {
  var [open, setOpen] = useState(false);
  return h('li', { className: 'tier-card__feature tier-card__feature--credit' },
    h('span', { className: 'tier-card__check' }, '✓'),
    h('span', {
      className: 'tier-card__credit-label',
      onClick: function(e) { e.stopPropagation(); setOpen(!open); }
    },
      props.text,
      h('span', { className: 'tier-card__credit-info' }, 'ⓘ')
    ),
    open ? h('div', { className: 'tier-card__credit-tooltip' },
      'AI-kreditter bruges til generering, kørsel, fixing og planlægning. Forbruget afhænger af den valgte model — capable (højere kvalitet) eller standard (hurtigere, billigere).'
    ) : null
  );
}

function TiersBlock(props) {
  var tiers = Array.isArray(props.tiers) ? props.tiers : [];
  var labels = props.labels || {};
  return h('div', { className: 'tiers-grid' },
    tiers.map(function(tier) {
      var features = Array.isArray(tier.features) ? tier.features : [];
      return h('article', {
        key: tier.name,
        className: 'tier-card' + (tier.highlighted ? ' tier-card--highlighted' : '')
      },
        tier.highlighted ? h('span', { className: 'tier-card__badge' }, labels.popular || 'Populær') : null,
        h('h3', { className: 'tier-card__name' }, tier.name || ''),
        h('p', { className: 'tier-card__description' }, tier.description || ''),
        h('div', { className: 'tier-card__price-row' },
          h('span', { className: 'tier-card__price' }, tier.price || ''),
          tier.period ? h('span', { className: 'tier-card__period' }, tier.period) : null
        ),
        h('ul', { className: 'tier-card__features' },
          features.map(function(f) {
            var isCredit = f.indexOf('AI-kredit') !== -1;
            if (isCredit) return h(CreditFeature, { key: f, text: f });
            return h('li', { key: f, className: 'tier-card__feature' },
              h('span', { className: 'tier-card__check' }, '✓'),
              f
            );
          })
        ),
        h('button', {
          className: 'btn ' + (tier.highlighted ? 'btn--primary' : 'btn--secondary') + ' tier-card__cta',
          type: 'button',
          onClick: function() { props.onSelectPlan ? props.onSelectPlan(tier.name) : props.onGetStarted(); }
        }, tier.cta || labels.defaultCta || 'Kom i gang')
      );
    })
  );
}

function PricingPlan(props) {
  var plan = props.plan || {};
  var features = Array.isArray(plan.features) ? plan.features : [];
  return h('article', { className: 'pricing-card' + (plan.featured ? ' pricing-card--featured' : '') },
    h('div', { className: 'pricing-card__top' },
      h('h2', { className: 'pricing-card__name' }, plan.name || ''),
      h('p', { className: 'pricing-card__summary' }, plan.summary || '')
    ),
    h('div', { className: 'pricing-card__price-row' },
      h('span', { className: 'pricing-card__price' }, plan.price || ''),
      plan.cadence ? h('span', { className: 'pricing-card__cadence' }, plan.cadence) : null
    ),
    h('div', { className: 'pricing-card__divider' }),
    h('ul', { className: 'pricing-card__features' },
      features.map(function(feature) {
        return h('li', { key: feature, className: 'pricing-card__feature' }, feature);
      })
    )
  );
}

function FaqItem(props) {
  var item = props.item || {};
  return h('article', { className: 'faq-item' },
    h('h2', { className: 'faq-item__question' }, item.question || ''),
    h('p', { className: 'faq-item__answer' }, item.answer || '')
  );
}

function MarketingPage(props) {
  var page = props.page || {};
  var labels = props.labels || {};
  var sections = Array.isArray(page.sections) ? page.sections : [];
  var plans = Array.isArray(page.plans) ? page.plans : [];
  var faqs = Array.isArray(page.faqs) ? page.faqs : [];
  var pageClass = 'marketing-page marketing-page--embedded' + (props.sectionId ? ' marketing-page--' + props.sectionId : '');
  return h('section', {
    id: props.sectionId || null,
    className: pageClass
  },
    h('div', { className: 'marketing-page__intro' },
      h('p', { className: 'marketing-page__eyebrow' }, page.eyebrow || ''),
      h('h2', { className: 'marketing-page__title' }, page.title || ''),
      h('p', { className: 'marketing-page__copy' }, page.intro || '')
    ),
    page.layout === 'showcase'
      ? h(ShowcaseSection, { sections: sections })
      : page.layout === 'cards'
        ? h(UseCaseGrid, { sections: sections })
        : page.layout === 'beta'
          ? h(BetaBlock, { perks: page.betaPerks || [], onGetStarted: props.onGetStarted })
          : page.layout === 'tiers'
            ? h(TiersBlock, { tiers: page.tiers || [], labels: labels, onGetStarted: props.onGetStarted, onSelectPlan: props.onSelectPlan })
            : sections.map(function(section, index) {
              return h(SectionGroup, { key: section.title, section: section, index: index });
            }),
    page.layout !== 'beta' && plans.length
      ? h('section', { className: 'marketing-section' },
          h('div', { className: 'marketing-section__header' },
            h('h2', { className: 'marketing-section__title' }, labels.plans || 'Planer')
          ),
          h('div', { className: 'pricing-grid' },
            plans.map(function(plan) {
              return h(PricingPlan, { key: plan.name, plan: plan });
            })
          )
        )
      : null,
    faqs.length
      ? h('section', { className: 'marketing-section' },
          h('div', { className: 'marketing-section__header' },
            h('h2', { className: 'marketing-section__title' }, labels.answers || 'Svar')
          ),
          h('div', { className: 'faq-list' },
            faqs.map(function(item) {
              return h(FaqItem, { key: item.question, item: item });
            })
          )
        )
      : null
  );
}

module.exports = MarketingPage;
